import { useEffect, useState, memo, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { supabase } from 'config/supabase'
import { useDispatch } from 'react-redux'
import { updateUserSession } from 'store/actions/auth.action'
import Image from 'next/image'
import { Dialog, DialogContent } from '@components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import { Separator } from '@components/ui/separator'
import { Wand2, Mail } from 'lucide-react'
import { notifyToast } from 'utils/responseUtils'
import { motion, AnimatePresence } from 'framer-motion'
import { isBrowser } from 'utils/isBrowser'

// Import modular components
import CollapsibleSection from './CollapsibleSection'
import EmailForm from './EmailForm'
import MagicLinkForm from './MagicLinkForm'
import SocialButtons from './SocialButtons'

// Animation variants
const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.3, ease: 'easeOut' },
}

function AuthModal({ type = 'sign_in', onClose }) {
  const dispatch = useDispatch()
  const { t } = useTranslation('common')
  const [showEmailLogin, setShowEmailLogin] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [activeTab, setActiveTab] = useState(type)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState({
    email: false,
    magic: false,
    discord: false,
    google: false,
  })

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        dispatch(updateUserSession(session))
        onClose()
      }
    })
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        dispatch(updateUserSession(session))
        onClose()
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  // Handlers
  const handleEmailSignIn = useCallback(
    async (e) => {
      e.preventDefault()
      setIsLoading((prev) => ({ ...prev, email: true }))
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        })
        if (error) {
          dispatch(notifyToast('error', error.message))
        } else {
          dispatch(notifyToast('success', t('auth.sign_in_success')))
        }
      } catch (error) {
        dispatch(notifyToast('error', t('auth.unexpected_error')))
      } finally {
        setIsLoading((prev) => ({ ...prev, email: false }))
      }
    },
    [email, password, dispatch, t]
  )

  const handleEmailSignUp = useCallback(
    async (e) => {
      e.preventDefault()
      setIsLoading((prev) => ({ ...prev, email: true }))
      try {
        // Get current page to redirect back to after auth
        const currentPath = isBrowser() ? window.location.pathname + window.location.search : '/'

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback?redirectTo=${encodeURIComponent(currentPath)}`,
          },
        })
        if (error) {
          dispatch(notifyToast('error', error.message))
        } else {
          dispatch(notifyToast('success', t('auth.sign_up_success')))
        }
      } catch (error) {
        dispatch(notifyToast('error', t('auth.unexpected_error')))
      } finally {
        setIsLoading((prev) => ({ ...prev, email: false }))
      }
    },
    [email, password, dispatch, t]
  )

  const handleMagicLinkAuth = useCallback(
    async (e) => {
      e.preventDefault()
      setIsLoading((prev) => ({ ...prev, magic: true }))
      try {
        // Get current page to redirect back to after auth
        const currentPath = isBrowser() ? window.location.pathname + window.location.search : '/'

        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback?redirectTo=${encodeURIComponent(currentPath)}`,
          },
        })
        if (!error) {
          setMagicLinkSent(true)
          dispatch(notifyToast('success', t('auth.check_email_signup')))
        } else {
          dispatch(notifyToast('error', error.message))
        }
      } catch (error) {
        dispatch(notifyToast('error', t('auth.unexpected_error')))
      } finally {
        setIsLoading((prev) => ({ ...prev, magic: false }))
      }
    },
    [email, dispatch, t]
  )

  const handleSocialAuth = useCallback(
    async (provider) => {
      setIsLoading((prev) => ({ ...prev, [provider]: true }))
      try {
        // Get current page to redirect back to after auth
        const currentPath = isBrowser() ? window.location.pathname + window.location.search : '/'

        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location.origin}/auth/callback?redirectTo=${encodeURIComponent(currentPath)}`,
          },
        })
        if (error) {
          dispatch(notifyToast('error', `${t('auth.unexpected_error')} ${provider}: ${error.message}`))
        }
      } catch (error) {
        dispatch(notifyToast('error', t('auth.unexpected_error')))
      } finally {
        setIsLoading((prev) => ({ ...prev, [provider]: false }))
      }
    },
    [dispatch, t]
  )

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 bg-white text-gray-900 border border-gray-200 shadow-xl rounded-lg max-h-[90vh] overflow-hidden">
        <motion.div
          className="p-6 overflow-y-auto max-h-[90vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="flex justify-center mb-8"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
          >
            <Image
              className="m-auto md:w-[240px] mb-2 w-[170px] object-contain"
              src="/assets/common/StyleyLogo.png"
              width={240}
              height={61}
              alt="Styley Logo"
              priority
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
            />
          </motion.div>

          <div className="text-center mb-8">
            <p className="text-gray-600 font-medium">{t('auth.head')}</p>
          </div>

          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid grid-cols-2 mb-8 bg-gray-100 rounded-sm">
              <TabsTrigger value="sign_in" className="data-[state=active]:bg-white rounded-sm">
                {t('auth.sign_in')}
              </TabsTrigger>
              <TabsTrigger value="sign_up" className="data-[state=active]:bg-white rounded-sm">
                {t('auth.sign_up')}
              </TabsTrigger>
            </TabsList>

            <AnimatePresence mode="wait">
              {activeTab === 'sign_in' && (
                <TabsContent
                  className="focus:outline-none focus:ring-0 focus-visible:ring-0"
                  value="sign_in"
                  forceMount
                >
                  <motion.div className="space-y-4" initial="initial" animate="animate" exit="exit" variants={fadeIn}>
                    <CollapsibleSection
                      title={t('auth.magic_link')}
                      icon={Wand2}
                      isOpen={showMagicLink}
                      onToggle={() => setShowMagicLink(!showMagicLink)}
                    >
                      <MagicLinkForm
                        email={email}
                        setEmail={setEmail}
                        isLoading={isLoading.magic}
                        onSubmit={handleMagicLinkAuth}
                        magicLinkSent={magicLinkSent}
                        setMagicLinkSent={setMagicLinkSent}
                      />
                    </CollapsibleSection>

                    <CollapsibleSection
                      title={t('auth.continue_with_email')}
                      icon={Mail}
                      isOpen={showEmailLogin}
                      onToggle={() => setShowEmailLogin(!showEmailLogin)}
                    >
                      <EmailForm
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        isLoading={isLoading.email}
                        onSubmit={handleEmailSignIn}
                        isSignUp={false}
                      />
                    </CollapsibleSection>

                    <div className="relative my-8">
                      <Separator className="bg-gray-200" />
                      <div className="relative flex justify-center">
                        <span className="bg-white px-3 text-xs uppercase text-gray-500 absolute -top-2.5 font-medium tracking-wider">
                          {t('auth.or')}
                        </span>
                      </div>
                    </div>

                    <SocialButtons handleSocialAuth={handleSocialAuth} isLoading={isLoading} isSignUp={false} />
                  </motion.div>
                </TabsContent>
              )}

              {activeTab === 'sign_up' && (
                <TabsContent value="sign_up" forceMount>
                  <motion.div className="space-y-4" initial="initial" animate="animate" exit="exit" variants={fadeIn}>
                    <CollapsibleSection
                      title={t('auth.magic_link')}
                      icon={Wand2}
                      isOpen={showMagicLink}
                      onToggle={() => setShowMagicLink(!showMagicLink)}
                    >
                      <MagicLinkForm
                        email={email}
                        setEmail={setEmail}
                        isLoading={isLoading.magic}
                        onSubmit={handleMagicLinkAuth}
                        magicLinkSent={magicLinkSent}
                        setMagicLinkSent={setMagicLinkSent}
                      />
                    </CollapsibleSection>

                    <CollapsibleSection
                      title={t('auth.sign_up_with_email')}
                      icon={Mail}
                      isOpen={showEmailLogin}
                      onToggle={() => setShowEmailLogin(!showEmailLogin)}
                    >
                      <EmailForm
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        isLoading={isLoading.email}
                        onSubmit={handleEmailSignUp}
                        isSignUp={true}
                      />
                    </CollapsibleSection>

                    <div className="relative my-8">
                      <Separator className="bg-gray-200" />
                      <div className="relative flex justify-center">
                        <span className="bg-white px-3 text-xs uppercase text-gray-500 absolute -top-2.5 font-medium tracking-wider">
                          {t('auth.or')}
                        </span>
                      </div>
                    </div>

                    <SocialButtons handleSocialAuth={handleSocialAuth} isLoading={isLoading} isSignUp={true} />
                  </motion.div>
                </TabsContent>
              )}
            </AnimatePresence>
          </Tabs>

          <div className="text-center text-xs text-gray-500 mt-8 px-2 leading-relaxed">{t('auth.terms_footer')}</div>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}

export default memo(AuthModal)
